import { useState, useEffect, useRef } from "react"
import HCRender from "aldoo-ra/HelpCenter/hc-render"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"

const Sidebar = ({
  rootCategoriesData,
  onArticleSelect,
  onCategorySelect,
  expandedCategories,
  currentPath,
}) => {
  const texts = TextBundle({ id: "help-center" })
  const HelpCenterBrowser = HCRender.getFragment("HelpCenterBrowser")
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const dropdownRef = useRef(null)

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)

  const handleCategorySelect = (categorySlug, parentPath) => {
    onCategorySelect(categorySlug, parentPath)
  }

  const handleArticleSelect = (article, parentPath) => {
    onArticleSelect(article, parentPath)
    setIsMobileMenuOpen(false)
  }

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [isMobileMenuOpen])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(".mobile-dropdown-toggle")
      ) {
        setIsMobileMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMobileMenuOpen])

  useEffect(() => {
    if (!isMobileMenuOpen || !dropdownRef.current) return
    const panel = dropdownRef.current
    let startY = 0
    let startTime = 0
    let moving = false
    let didSwipeDown = false
    let hasDragged = false

    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY
      startTime = new Date().getTime()
      moving = true
      didSwipeDown = false
      hasDragged = false
    }

    const handleTouchMove = (e) => {
      if (!moving) return
      const currentY = e.touches[0].clientY
      const deltaY = currentY - startY
      if (deltaY > 0) {
        if (!hasDragged) {
          panel.style.transition = "none"
          hasDragged = true
        }
        e.preventDefault()
        panel.style.transform = `translateY(${deltaY}px)`
        const timeDiff = new Date().getTime() - startTime
        if (deltaY > 80 || (timeDiff < 300 && deltaY > 40)) didSwipeDown = true
      }
    }

    const handleTouchEnd = () => {
      if (!moving) return
      moving = false
      if (didSwipeDown) {
        panel.style.transition = "transform 300ms ease-out"
        panel.style.transform = "translateY(100%)"
        setTimeout(() => setIsMobileMenuOpen(false), 300)
      } else if (hasDragged) {
        panel.style.transition = "transform 200ms ease-out"
        panel.style.transform = "translateY(0)"
      }
    }

    panel.addEventListener("touchstart", handleTouchStart, { passive: false })
    panel.addEventListener("touchmove", handleTouchMove, { passive: false })
    panel.addEventListener("touchend", handleTouchEnd)

    return () => {
      panel.removeEventListener("touchstart", handleTouchStart)
      panel.removeEventListener("touchmove", handleTouchMove)
      panel.removeEventListener("touchend", handleTouchEnd)
    }
  }, [isMobileMenuOpen])

  const mobileStyles = `
    .mobile-overlay { transition: opacity 250ms ease-out; opacity: 0; pointer-events: none; }
    .mobile-overlay.visible { opacity: 0.5; pointer-events: auto; }
    .mobile-panel { transition: transform 250ms cubic-bezier(0.16, 1, 0.3, 1); transform: translateY(100%); pointer-events: none; }
    .mobile-panel.visible { transform: translateY(0); pointer-events: auto; }
    .drag-handle { width: 48px; height: 5px; background-color: #D1D5DB; border-radius: 9999px; margin: 0 auto 16px; }
  `

  return (
    <>
      {/* Mobile */}
      <div className="block lg:hidden w-full mb-6 md:mb-8">
        <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />
        <div
          onClick={toggleMobileMenu}
          className="max-w-fit h-12 flex items-center justify-start gap-2 bg-paper rounded-full border border-secondary cursor-pointer px-4 mobile-dropdown-toggle"
        >
          <Typography variant="h3" className="text-text">
            {texts?.sidebar_heading || "Articles"}
          </Typography>
          <ChevronDownIcon
            className={`w-5 h-5 text-text_secondary transition-transform ${isMobileMenuOpen ? "rotate-180" : ""}`}
          />
        </div>

        <div
          className={`fixed inset-0 bg-black z-[1000000000] mobile-overlay ${isMobileMenuOpen ? "visible" : ""}`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        <div
          ref={dropdownRef}
          className={`fixed bottom-0 left-0 right-0 bg-paper rounded-t-[24px] z-[1000000001] mobile-panel ${isMobileMenuOpen ? "visible" : ""}`}
          style={{ height: "70vh" }}
        >
          <div className="h-full">
            <div className="absolute top-0 left-0 right-0 z-10 bg-paper px-5 pt-5 pb-2 rounded-t-[24px]">
              <div className="drag-handle" />
              <Typography variant="h3" className="text-text mb-2">
                {texts?.sidebar_heading || "Articles"}
              </Typography>
            </div>

            <div
              className="absolute left-0 right-0 px-5 overflow-y-auto"
              style={{ top: "80px", bottom: "64px", overscrollBehavior: "contain" }}
            >
              {rootCategoriesData && HelpCenterBrowser && (
                <HelpCenterBrowser
                  rootCategoriesData={rootCategoriesData}
                  onArticleSelect={handleArticleSelect}
                  onCategorySelect={handleCategorySelect}
                  expandedCategories={expandedCategories || {}}
                  currentPath={currentPath}
                  isMobile={true}
                />
              )}
              <div className="h-8"></div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 w-full bg-paper px-4 py-6">
              <Button
                onClick={() => setIsMobileMenuOpen(false)}
                variant="secondary"
                className="w-full max-w-[360px] mx-auto"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden lg:block w-1/4 lg:w-[346px] xl:w-[360px] border-r border-bordered flex-shrink-0 overflow-y-auto pr-10">
        <div className="sticky top-0 pt-2 pb-4">
          <Typography variant="h3" className="text-text text-left">
            {texts?.sidebar_heading || "Articles"}
          </Typography>
        </div>
        {rootCategoriesData && HelpCenterBrowser && (
          <HelpCenterBrowser
            rootCategoriesData={rootCategoriesData}
            onArticleSelect={onArticleSelect}
            onCategorySelect={onCategorySelect}
            expandedCategories={expandedCategories || {}}
            currentPath={currentPath}
            isMobile={false}
          />
        )}
      </div>
    </>
  )
}

export default Sidebar
